import * as R from 'ramda';
import Log from '../../common/logging';
import { Routes } from '../../config/routes';
import { ScopeRequest, ScopeRequestStatus } from '../../services/secureIdentityProvider';
import {
  Step,
  Validation,
  ValidationStatus,
  ValidationStep,
  ValidationStepStatus,
} from '../../services/validation/validationService';
import { ContextValidationStatus, ExtendedValidationStatuses } from './contextValidationStatus';
import FlowMode from './FlowMode';

/** Map validation steps to routes  */
type StepsRouteMap = {
  route: Routes;
  steps: Array<ValidationStep>;
};

/** Array of validation route mappings */
const documentValidationRouteMappings: Array<StepsRouteMap> = [
  {
    route: Routes.DOCUMENT_SELFIE,
    steps: [ValidationStep.identityToken, ValidationStep.selfie],
  },
  {
    route: Routes.DOCUMENT_CAPTURE,
    steps: [ValidationStep.appId, ValidationStep.document],
  },
  {
    route: Routes.DECLARATIONS,
    steps: [ValidationStep.terms],
  },
  {
    route: Routes.EMAIL_CAPTURE,
    steps: [ValidationStep.email, ValidationStep.emailToken],
  },
  {
    route: Routes.FACE_UNIQUENESS,
    steps: [ValidationStep.uniqueness],
  },
  {
    route: Routes.FACE_AUTH,
    steps: [ValidationStep.faceAuth],
  },
];

/** Routes that could be performed on mobile */
const continueOnMobileAllowedRoutes: Array<Routes> = [
  Routes.DOCUMENT_SELFIE,
  Routes.DOCUMENT_CAPTURE,
  Routes.FACE_UNIQUENESS,
  Routes.FACE_AUTH,
];

/** Flow result, returns route and remaining steps */
export type FlowResult = {
  route: Routes;
  remainingSteps: Array<ValidationStep>;
};

// filter for awaiting user input, awaiting dependency and invalid statuses when getting pending flows to process, since we are not updating the step list between patching each step
const pendingFlowStatuses = [
  ValidationStepStatus.awaitingUserInput,
  ValidationStepStatus.awaitingDependency,
  ValidationStepStatus.invalid,
];

/** Create a final flow result with a given route and no remaining steps */
const finalFlowResult = (route: Routes): FlowResult => ({ route, remainingSteps: [] });

/** Return steps with pending statuses */
const getValidationPendingSteps = (steps: Step[]): ValidationStep[] =>
  steps.filter((step) => R.contains(step.status, pendingFlowStatuses)).map((step) => step.name);

function showContinueOnMobileSelector({
  validation,
  flowMode,
  continueOnMobileEnabled,
}: {
  validation: Validation;
  flowMode: FlowMode;
  continueOnMobileEnabled: boolean;
}): boolean {
  const mappings = getValidationPendingSteps(validation.steps).map((step) =>
    R.find((map) => R.contains(step, map.steps), documentValidationRouteMappings),
  );
  const allowedRouted = mappings.filter((map) => R.contains(map?.route, continueOnMobileAllowedRoutes));
  return continueOnMobileEnabled && allowedRouted.length > 0 && flowMode === FlowMode.userSelect;
}

/**
 * Return the list of pending routes that can be continued on mobile.
 * This will be ordered according to the sequence defined in documentValidationRouteMappings
 *
 * Note: There is currently a bug where the selfie step can only be patched after the document step.
 * This means that if the user completes the selfie but doesn't finish the document steps then the
 * selfie step will not be patched. This method ensures the selfie route is always returned before the document
 * route.
 */
function getPendingContinueOnMobileRoutes(validationPendingSteps: ValidationStep[]): Routes[] {
  // iterate through the pending routes in order
  const pendingRoutes = R.pipe(
    // filter for routes with matching pending steps
    R.filter<StepsRouteMap>(
      (mapping: StepsRouteMap) => !R.isEmpty(R.intersection(mapping.steps, validationPendingSteps)),
    ),
    R.map((mapping: StepsRouteMap) => mapping.route),
    // remove duplicates
    R.uniq,
    // only return routes that are allowed for continue on mobile
    R.intersection(continueOnMobileAllowedRoutes),
  )(documentValidationRouteMappings);
  return pendingRoutes;
}

/** Return all the steps that are associated with a route */
function getAllStepsForRoute(route: Routes): Array<ValidationStep> {
  const routeMapping = R.find((mapping: StepsRouteMap) => mapping.route === route, documentValidationRouteMappings);
  return routeMapping?.steps || [];
}

function getStepsCompletedFlow(flowMode: FlowMode): FlowResult {
  const finalRoute = flowMode === FlowMode.partial ? Routes.UPLOAD_COMPLETE : Routes.VALIDATION_IN_PROGRESS;
  return finalFlowResult(finalRoute);
}

/** List of ExtendedValidationStatuses for failure roots */
const failureStatuses: ExtendedValidationStatuses[] = [
  ValidationStatus.failed,
  ContextValidationStatus.failedStepInvalid,
  ContextValidationStatus.unknownError,
];

/** Return the relevant failure flow given the validation status and flow mode */
function getFailureFlow(validationStatus: ExtendedValidationStatuses, flowMode: FlowMode): FlowResult {
  // Always return the document error route when in the partial flow
  if (flowMode === FlowMode.partial) {
    return finalFlowResult(Routes.VALIDATION_ERROR);
  }

  // Route to the unknown error screen if the status is unknownError, otherwise show the rejected screen
  return finalFlowResult(
    validationStatus === ContextValidationStatus.unknownError ? Routes.VALIDATION_ERROR : Routes.VALIDATION_REJECTED,
  );
}

/** Get the next flow to present for an in progress validation */
function getNextFlow(
  validationStatus: ExtendedValidationStatuses,
  validationPendingSteps: Array<ValidationStep>,
  flowMode: FlowMode,
): FlowResult {
  // If the validation is failed or unknown, then show the error screen
  if (R.contains(validationStatus, failureStatuses)) {
    return getFailureFlow(validationStatus, flowMode);
  }

  // If there are no flows remaining, show the in progress screen
  if (R.isEmpty(validationPendingSteps)) {
    return getStepsCompletedFlow(flowMode);
  }

  const nextPendingStep = R.head(validationPendingSteps);
  const routeMapping = R.find((map) => R.contains(nextPendingStep, map.steps), documentValidationRouteMappings);
  // If no mapping is found, show the error screen and log an error
  if (!routeMapping) {
    Log.error(`Error routing to next flow. No route mapping found for pending step: ${nextPendingStep}`);
    return finalFlowResult(Routes.VALIDATION_REJECTED);
  }

  return {
    route: routeMapping.route,
    // return remaining pending steps excluding the steps from the route mapping result
    remainingSteps: R.filter((step) => !R.contains(step, routeMapping.steps), validationPendingSteps),
  };
}

/** Return initial flow based on the current pending validation step in the validation */
function getInitialFlowForCurrentStep(validation: Validation, flowMode: FlowMode): FlowResult {
  const validationPendingSteps = getValidationPendingSteps(validation.steps);
  // The terms step is always processed first
  if (R.contains(ValidationStep.terms, validationPendingSteps)) {
    return {
      route: Routes.DECLARATIONS,
      // Return pending steps excluding terms
      remainingSteps: R.filter((step) => step !== ValidationStep.terms, validationPendingSteps),
    };
  }

  // if there are any completed steps, show the continue screen
  if (R.any((step) => step.status === ValidationStepStatus.accepted, validation.steps)) {
    return {
      route: Routes.CONTINUE,
      remainingSteps: validationPendingSteps,
    };
  }

  // navigate to the next flow based on the pending steps
  return getNextFlow(validation.status, validationPendingSteps, flowMode);
}

function getValidationCompletedRoute({
  scopeRequest,
  showPIIShareAuthorization,
}: {
  scopeRequest?: ScopeRequest | null;
  showPIIShareAuthorization: boolean;
}): Routes {
  // finalize the validation if there is no scope request or if the scope request is already resolved
  if (!scopeRequest || scopeRequest.status === ScopeRequestStatus.VERIFICATION_SUCCESS) {
    return Routes.VALIDATION_FINALIZING;
  }

  // if there is an unresolved scope request, ask the user to confirm sharing information
  // If the flow config does not require PII sharing, then skip the sharing screen
  return showPIIShareAuthorization ? Routes.SHARE_PERSONAL_DETAILS : Routes.VALIDATION_FINALIZING;
}

/** Return relevant route based on validation status */
function getRouteForValidationStatus({
  status,
  scopeRequest,
  showPIIShareAuthorization = false,
}: {
  status: ExtendedValidationStatuses;
  scopeRequest?: ScopeRequest;
  showPIIShareAuthorization?: boolean;
}): Routes {
  switch (status) {
    case ValidationStatus.processing:
    case ContextValidationStatus.poll:
      return Routes.VALIDATION_IN_PROGRESS;
    case ValidationStatus.inReview:
      return Routes.VALIDATION_IN_REVIEW;
    case ValidationStatus.completed:
      return getValidationCompletedRoute({
        scopeRequest,
        showPIIShareAuthorization,
      });
    case ContextValidationStatus.unknown:
    case ContextValidationStatus.unknownError:
      return Routes.VALIDATION_ERROR;
    case ValidationStatus.collecting:
    case ValidationStatus.failed:
    case ContextValidationStatus.failedStepInvalid: {
      return Routes.VALIDATION_REJECTED;
    }
    default:
      return Routes.VALIDATION_REJECTED;
  }
}

/** Return the initial flow to present for a validation */
function getInitialFlowForValidation({
  validation,
  flowMode,
  scopeRequest,
  showPIIShareAuthorization = false,
}: {
  validation: Validation;
  flowMode: FlowMode;
  scopeRequest?: ScopeRequest;
  showPIIShareAuthorization?: boolean;
}): FlowResult {
  switch (validation.status) {
    case ValidationStatus.collecting:
      return getInitialFlowForCurrentStep(validation, flowMode);
    default:
      return finalFlowResult(
        getRouteForValidationStatus({
          status: validation.status,
          scopeRequest,
          showPIIShareAuthorization,
        }),
      );
  }
}

export default {
  getValidationPendingSteps,
  getInitialFlowForCurrentStep,
  getInitialFlowForValidation,
  getRouteForValidationStatus,
  getNextFlow,
  getPendingContinueOnMobileRoutes,
  getAllStepsForRoute,
  showContinueOnMobileSelector,
  getValidationCompletedRoute,
};
